const throttle = require('lodash/throttle');

const className = 'is-scrolling';
const STATE_ON = 'on';
const STATE_OFF = 'off';

/**
 * Creates a new scrollEndEventDispatcher. An object with this function:
 * - watch()
 *
 * Its goal is to wait a timeout and fire a scrollend pseudoevent
 * @param {Function} onEndFunction - scrollend handler
 * @returns {object} scrollEndEventDispatcher
 */
function createScrollEndEventDispatcher(onEndFunction) {
  const SCROLLEND_EVENT_TIMEOUT = 500;
  const SCROLLEND_RESET_TIMEOUT = 200;

  let timeoutScrollStopId = null;

  /**
   * Cancels the execution of `onEndFunction` from a previous scheduling.
   * @returns {void}
   */
  function cancelEventDispatch() {
    clearTimeout(timeoutScrollStopId);
  }

  /**
   * Schedule the execution of `onEndFunction` after event timeout fires.
   * @returns {void}
   */
  function setupEventDispatch() {
    timeoutScrollStopId = setTimeout(() => {
      onEndFunction();
      timeoutScrollStopId = null;
    }, SCROLLEND_EVENT_TIMEOUT);
  }

  return {
    /**
     * If a new watcher is fired after SCROLLEND_RESET_TIMEOUT, then the event
     * dispatch will be reset.
     * Otherwise, the timeout will finish between 300 and 500 milliseconds after
     * the last watch command that setup the event dispatch.
     * In that manner it will avoid to flickers cause by an early scrollEnd
     * event.
     * @returns {void}
     */
    watch: throttle(
      () => {
        cancelEventDispatch();
        setupEventDispatch();
      },
      SCROLLEND_RESET_TIMEOUT,
    ),
  };
}

function createScrollMonitor(element) {
  const classlistTarget = element === document ? element.documentElement : element;
  const scrollEndEventDispatcher = createScrollEndEventDispatcher(handleScrollEnd);

  let state = STATE_OFF;

  /**
   * Set the monitor in scrolling state
   */
  function on() {
    state = STATE_ON;
    classlistTarget.classList.add(className);
  }

  /**
   * Set the monitor out of scrolling state
   */
  function off() {
    state = STATE_OFF;
    classlistTarget.classList.remove(className);
  }

  /**
   * Check if monitor is in scrolling state
   * @returns {boolean}
   */
  function isScrolling() {
    return state === STATE_ON;
  }

  /**
   * Scroll event handler function
   */
  function handleScroll() {
    if (!isScrolling()) on();
    scrollEndEventDispatcher.watch();
  }

  /**
   * Scrollend pseudoevent handler function
   */
  function handleScrollEnd() {
    off();
  }

  // Scroll will be monitorized during whole lifecycle
  // eslint-disable-next-line listeners/no-missing-remove-event-listener
  element.addEventListener('scroll', handleScroll, { passive: true });

  return {
    on,
    off,
    isScrolling,
  };
}

let mainMonitor = null;

module.exports = {
  createScrollMonitor,
  init: function init() {
    if (mainMonitor) return;
    mainMonitor = createScrollMonitor(document);
  },
  isScrolling: function isScrolling() {
    if (!mainMonitor) throw new Error('Not active monitoring');
    return mainMonitor.isScrolling();
  },
};
